import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { ColumnView } from './ColumnView';

// Notion-style multi-column layout (invisible container). Schema follows the
// proven design: `column` content is `block+` (arbitrary blocks); the row uses
// `column+` (NOT `{2,}` — that invalidates docs on partial paste, ProseMirror
// #782), and the "≥2 columns or unwrap" rule is enforced in commands.ts.
// `isolating` keeps edits/selection scoped to a column; the row is `defining`
// so paste keeps the wrapper.

// A single column. Width is a fraction (0..1) of the row; columns sum to 1.
export const Column = Node.create({
  name: 'column',
  content: 'block+',
  isolating: true,
  selectable: false, // you select the row, not bare columns
  addAttributes() {
    return {
      width: {
        default: null,
        parseHTML: (el) => {
          const w = parseFloat((el as HTMLElement).dataset.width ?? '');
          return Number.isFinite(w) ? w : null;
        },
        renderHTML: (attrs) => (attrs.width != null ? { 'data-width': String(attrs.width) } : {}),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-type="column"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'column' }), 0];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ColumnView);
  },
});

// The row container. Rendered as a plain flex div (no React NodeView) so its
// column children are direct flex items; layout/affordances are pure CSS
// (.rc-columns) + the per-column NodeView. Invisible in the export.
export const Columns = Node.create({
  name: 'columns',
  group: 'block',
  content: 'column+',
  isolating: true,
  defining: true,
  selectable: true,
  parseHTML() {
    return [{ tag: 'div[data-type="columns"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'columns', class: 'rc-columns' }), 0];
  },
});
